/**
 * Bordereau Store - Zustand State Management
 * 
 * Manages all state for the bordereau generation feature including:
 * - Navigation between steps
 * - Document selection and ordering
 * - Stamp configuration
 * - Numbering (manual/AI)
 * - Generation status
 */

import { create } from 'zustand';
import { comparePieceNumbers, reflowAfterReorder } from '@hector/piece-number-sort';
import type {
  Bordereau,
  Stamp,
  StampAbsolutePosition,
  JobStatus,
  BordereauStep,
  SelectedDocument,
  NumberingMode,
  CoverPageMetadata,
  AdminCoverMetadata,
  RequestedFormat,
  BordereauMode,
} from '../types/bordereau.types';
import { STAMP_DEFAULT_POSITION, STAMP_SCALE } from '../constants/bordereau.constants';
import { sortSelectedDocumentsForBordereau } from '../utils/bordereau.utils';
// Détection « Décision(s) attaquée(s) » — source canonique partagée
// (constantes + prédicat par chemin, catégorie-agnostique). Le dossier vit
// dans l'onglet « Juridiction et greffe » depuis 2026-07-14 ; la détection par
// display_folder_path est inchangée.

/**
 * Sorts document IDs: numbered first (ascending, via the shared canonical
 * piece-number comparator — tolerant of 2. / 2- / 2_ / 2bis…), unnumbered
 * preserve original order.
 */
function sortDocumentOrder(
  order: string[],
  numbers: Record<string, { number: string }>,
): string[] {
  const originalIndexMap = new Map(order.map((id, idx) => [id, idx]));
  return [...order].sort((a, b) => {
    const numA = numbers[a]?.number || '';
    const numB = numbers[b]?.number || '';
    const hasA = numA.length > 0;
    const hasB = numB.length > 0;
    if (hasA && hasB) return comparePieceNumbers(numA, numB);
    if (hasA && !hasB) return -1;
    if (!hasA && hasB) return 1;
    return (originalIndexMap.get(a) || 0) - (originalIndexMap.get(b) || 0);
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// STATE INTERFACE
// ═══════════════════════════════════════════════════════════════════════════

interface BordereauState {
  // Navigation
  isOpen: boolean;
  currentStep: BordereauStep;
  /**
   * Onglet du dossier à activer après fermeture du panel, posé par le bouton
   * « Voir dans Mes bordereaux » de l'écran d'export (`GenerationStep`).
   * Consommé une fois par `DossierView` (lu puis effacé) — le panel n'a pas
   * de référence directe à l'onglet actif du dossier, qui vit dans un state
   * local de `DossierView`, monté comme un sibling de `BordereauPanel` par
   * `CaseView`.
   */
  pendingSectionNavigation: 'bordereaux' | null;

  // Droit Public / Télérecours context
  /** `procedure_type` du dossier, passé à l'ouverture. `null` = droit privé. */
  procedureType: string | null;
  /** Mode choisi par l'avocat : 'communication' (défaut), 'productions', 'recapitulatif'. */
  bordereauMode: BordereauMode;
  /** ID du bordereau parent sélectionné en mode récapitulatif. */
  parentBordereauId: string | null;
  /**
   * Fichier Word/PDF du bordereau parent **uploadé manuellement** par l'avocat
   * en mode récapitulatif quand AUCUN bordereau antérieur n'existe dans Hector
   * (le confrère a fait ses précédents bordereaux sur RPVA/papier). Le supplément
   * généré par Hector vient s'apposer après ce fichier — pour l'instant en
   * livraison séparée (le métier les agrafe dans Word), une concaténation
   * automatique est un follow-up.
   */
  uploadedParentBordereauFile: File | null;

  // Bordereau data (synced with backend)
  bordereau: Bordereau | null;
  bordereauId: string | null;
  caseId: string | null;
  
  // Documents
  selectedDocuments: SelectedDocument[];
  documentOrder: string[];
  excludedDocumentIds: Set<string>;
  /**
   * Renuméroter en déplaçant (ON par défaut, 2026-07-14). Quand ON, un
   * glisser-déposer réorganise les numéros en suivant la norme de la pièce du
   * haut (`reorderAndReflow`). Échappatoire OFF : réordonner l'affichage sans
   * toucher aux numéros — utile juste après une extraction IA.
   */
  renumberOnDrag: boolean;

  // Stamp configuration
  availableStamps: Stamp[];
  selectedStampId: string | null;
  stampAbsolutePosition: StampAbsolutePosition;
  stampPerPiecePositions: Record<string, StampAbsolutePosition>;
  showNumberInStamp: boolean;
  /** Tamponnage avancé : apposer le tampon sur toutes les pages de chaque pièce (défaut: page 1 seule). */
  stampAllPages: boolean;

  // Numbering
  numberingMode: NumberingMode;
  pieceNumbers: Record<string, { number: string; label?: string }>;
  lastSwap: { fromDocId: string; toDocId: string; fromNumber: string; toNumber: string } | null;
  /**
   * IDs des pièces hérités d'un bordereau parent en mode `recapitulatif` —
   * leur `piece_number` est FIGÉ (Conseil d'État : la numérotation doit être
   * cumulative et stable au fil des productions complémentaires). `applyQuickNumbering`,
   * `setPieceNumber` et toutes les opérations de re-numérotation DOIVENT les
   * sauter. Vide en mode `communication` / `productions` initial.
   */
  lockedDocumentIds: Set<string>;
  
  // Generation
  generationStatus: JobStatus;
  generationJobId: string | null;
  generationProgress: number;
  currentGeneratingDoc: string;
  downloadUrl: string | null;
  
  // Options
  includeIndexPage: boolean;

  /**
   * Cover page metadata (TICKET-259) — saisi dans step 3 quand l'utilisateur
   * choisit une carte avec bordereau (page de garde). Réinitialisé sur openPanel/reset.
   */
  coverPageMetadata: CoverPageMetadata;
  /**
   * Métadonnées spécifiques au **bordereau de productions Télérecours** (admin).
   * Structure totalement distincte du civil (POUR / Ayant pour Avocat / CONTRE
   * + en-tête juridictionnel) — saisie en step 3 quand l'utilisateur est en
   * `mode='productions'` ou `'recapitulatif'`. Réinitialisé sur openPanel/reset.
   */
  adminCoverMetadata: AdminCoverMetadata;
  /**
   * Formulaires auxquels le pré-remplissage du bloc avocat (depuis le bloc
   * mémoire `user_profile`) a **déjà** été proposé dans cette session de panel.
   *
   * **Pourquoi ce souvenir existe** (2026-08-16) : « jamais renseigné » et
   * « vidé par l'avocat » sont tous deux la chaîne vide. Une simple garde de
   * vacance ne peut pas les séparer, et l'effet de pré-remplissage — qui se
   * rejoue à chaque remontage de l'étape 3 — réinstallait donc la mention que
   * l'avocat venait d'effacer. Vider un champ est un acte délibéré, au même
   * titre qu'une saisie : ce drapeau est ce qui rend le vidage durable.
   * Ne pas le simplifier en « le champ est-il vide ? » — c'est précisément le
   * test qui ne suffit pas.
   *
   * **Un drapeau par formulaire**, jamais un seul partagé : l'avocat édite deux
   * champs distincts, dans deux formulaires distincts (`notre_avocat` civil et
   * `avocat` administratif) ; ce qu'il fait de l'un ne dit rien de l'autre.
   *
   * **Portée volontairement courte** : cette session de panel. Le souvenir vit
   * exactement aussi longtemps que la valeur dont il parle — donc il est remis
   * à zéro partout où les métadonnées de page de garde le sont (`reset`,
   * `resetCoverPageMetadata`, `resetAdminCoverMetadata`). Rien n'est persisté :
   * un vidage sur un dossier ne doit pas priver le dossier suivant du
   * pré-remplissage.
   */
  avocatPrefillOffered: { civil: boolean; admin: boolean };
  /**
   * Format que l'utilisateur a explicitement demandé via les cards de step 3.
   * `null` = aucun choix encore. Pilote l'auto-download après génération.
   */
  requestedFormat: RequestedFormat | null;

  // Error state
  error: { code: string; message: string } | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// ACTIONS INTERFACE
// ═══════════════════════════════════════════════════════════════════════════

interface BordereauActions {
  // Navigation
  /**
   * Opens the bordereau panel for the given documents.
   *
   * @param documents      - Documents to include in the bordereau.
   * @param caseId         - Case identifier.
   * @param procedureType  - Optional `procedure_type` from the Case. Quand la matière est
   *                         administrative au sens de `isPublicLawProcedureType()`, le panel
   *                         affiche l'écran de choix de mode Télérecours. C'est `BordereauPanel`
   *                         qui applique le matcher — ce champ n'est qu'un repli quand l'appelant
   *                         connaît déjà la matière ; le `Case` refetché fait foi.
   */
  openPanel: (documents: SelectedDocument[], caseId: string, procedureType?: string | null) => void;

  /** Sets the Télérecours bordereau mode chosen by the user. */
  setBordereauMode: (mode: BordereauMode) => void;

  /** Sets the selected parent bordereau ID for mode récapitulatif. */
  setParentBordereauId: (id: string | null) => void;
  /** Sets the uploaded parent bordereau file (récap sans antécédent Hector). */
  setUploadedParentBordereauFile: (file: File | null) => void;
  closePanel: () => void;
  goToStep: (step: BordereauStep) => void;
  nextStep: () => void;
  prevStep: () => void;
  /** Pose la demande de bascule vers l'onglet « Mes bordereaux » du dossier. */
  requestSectionNavigation: (section: 'bordereaux') => void;
  /** Consomme la demande de bascule — appelé par `DossierView` après lecture. */
  clearSectionNavigationRequest: () => void;
  
  // Document actions
  reorderDocuments: (newOrder: string[]) => void;
  /**
   * Réordonne ET renumérote en repartant du numéro de la tête de liste d'avant le déplacement.
   * Utilisé par le glisser-déposer quand `renumberOnDrag` est ON. Saute les
   * pièces exclues et ne touche JAMAIS une numérotation récap figée (locked).
   */
  reorderAndReflow: (newOrder: string[]) => void;
  /** Bascule « Renuméroter en déplaçant ». */
  setRenumberOnDrag: (value: boolean) => void;
  removeDocument: (documentId: string) => void;
  addDocuments: (documents: SelectedDocument[]) => void;
  toggleDocumentExclusion: (documentId: string) => void;
  renameDocument: (documentId: string, name: string) => void;
  /**
   * Resynchronise numéros ET noms depuis la DB sans casser le reste du panel.
   * Appelé quand une écriture externe (tools agents `set_piece_numbers` /
   * `rename_pieces`) a muté les pièces pendant que le panel est ouvert.
   */
  reseedFromDatabase: (fresh: SelectedDocument[]) => void;
  
  // Stamp actions
  setStamps: (stamps: Stamp[]) => void;
  selectStamp: (stampId: string | null) => void;
  setStampAbsolutePosition: (position: StampAbsolutePosition) => void;
  setShowNumberInStamp: (show: boolean) => void;
  setStampAllPages: (value: boolean) => void;
  setStampPerPiecePosition: (documentId: string, position: StampAbsolutePosition) => void;
  clearStampPerPiecePosition: (documentId: string) => void;
  resetAllPerPiecePositions: () => void;
  
  // Numbering actions
  setNumberingMode: (mode: NumberingMode) => void;
  setPieceNumber: (documentId: string, number: string, label?: string) => void;
  applyQuickNumbering: (pattern: 'numeric' | 'alpha' | 'piece') => void;
  clearAllNumbers: () => void;
  clearLastSwap: () => void;
  /**
   * Pose la liste figée des `documentId` hérités du bordereau parent en
   * mode `recapitulatif`. Appelé par NumberingStep dès que la chaîne de
   * versions est connue. Reset() le remet à un Set vide.
   */
  setLockedDocumentIds: (ids: Iterable<string>) => void;

  // Generation actions
  setGenerationStatus: (status: JobStatus) => void;
  setGenerationJobId: (jobId: string | null) => void;
  setGenerationProgress: (progress: number, docName: string) => void;
  setDownloadUrl: (url: string) => void;
  resetGeneration: () => void;
  
  // Options
  setIncludeIndexPage: (include: boolean) => void;

  // Cover page (TICKET-259)
  setCoverPageMetadataField: (field: keyof CoverPageMetadata, value: string) => void;
  resetCoverPageMetadata: () => void;
  setAdminCoverMetadataField: (field: keyof AdminCoverMetadata, value: string) => void;
  resetAdminCoverMetadata: () => void;
  /** Marque le formulaire comme ayant reçu sa proposition de bloc avocat.
   *  Cf. `avocatPrefillOffered` et `hooks/useAvocatProfilePrefill.ts`. */
  markAvocatPrefillOffered: (form: 'civil' | 'admin') => void;
  setRequestedFormat: (format: RequestedFormat | null) => void;
  
  // Error handling
  setError: (error: { code: string; message: string } | null) => void;
  clearError: () => void;
  
  // Sync with backend
  setBordereau: (bordereau: Bordereau) => void;
  setBordereauId: (id: string) => void;
  
  // Validation helpers
  canProceedToStep: (step: BordereauStep) => boolean;
  getNumberedPiecesCount: () => number;
  
  // Reset
  reset: () => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// INITIAL STATE
// ═══════════════════════════════════════════════════════════════════════════

const initialState: BordereauState = {
  isOpen: false,
  currentStep: 1,
  pendingSectionNavigation: null,
  procedureType: null,
  bordereauMode: 'communication',
  parentBordereauId: null,
  uploadedParentBordereauFile: null,
  bordereau: null,
  bordereauId: null,
  caseId: null,
  selectedDocuments: [],
  documentOrder: [],
  excludedDocumentIds: new Set<string>(),
  renumberOnDrag: true,
  availableStamps: [],
  selectedStampId: null,
  stampAbsolutePosition: {
    x_mm: STAMP_DEFAULT_POSITION.X_MM,
    y_mm: STAMP_DEFAULT_POSITION.Y_MM,
    scale: STAMP_SCALE.DEFAULT,
  },
  stampPerPiecePositions: {},
  showNumberInStamp: true,
  stampAllPages: false,
  numberingMode: 'manual',
  pieceNumbers: {},
  lastSwap: null,
  lockedDocumentIds: new Set<string>(),
  generationStatus: 'not_started',
  generationJobId: null,
  generationProgress: 0,
  currentGeneratingDoc: '',
  downloadUrl: null,
  includeIndexPage: false,
  coverPageMetadata: {},
  adminCoverMetadata: {},
  avocatPrefillOffered: { civil: false, admin: false },
  requestedFormat: null,
  error: null,
};

// ═══════════════════════════════════════════════════════════════════════════
// STORE
// ═══════════════════════════════════════════════════════════════════════════

export const useBordereauStore = create<BordereauState & BordereauActions>((set, get) => ({
  ...initialState,

  // ═══════════════════════════════════════════════════════════════════════════
  // NAVIGATION
  // ═══════════════════════════════════════════════════════════════════════════

  openPanel: (documents, caseId, procedureType = null) => {
    // Seed pieceNumbers from existing DB values so swap logic can find them
    const seededNumbers: Record<string, { number: string }> = {};
    documents.forEach((d) => {
      if (d.piece_number?.trim()) {
        seededNumbers[d.id] = { number: d.piece_number.trim() };
      }
    });

    // Auto-ordre à l'ouverture, calqué sur la liste du dossier : numéros en
    // ordre naturel (2, 2-1, 2.1, 3bis, 3°4…) puis pièces sans numéro en ordre
    // chronologique (date extraite de l'intitulé). L'avocat part d'une liste
    // déjà rangée ; il peut ensuite drag-reorder, trier chrono ou renuméroter.
    const orderedIds = sortSelectedDocumentsForBordereau(documents);

    set({
      isOpen: true,
      currentStep: 1,
      caseId,
      procedureType: procedureType ?? null,
      // Reset mode to default on every open — the mode-choice step will set it.
      bordereauMode: 'communication',
      parentBordereauId: null,
      uploadedParentBordereauFile: null,
      selectedDocuments: documents,
      documentOrder: orderedIds,
      pieceNumbers: seededNumbers,
      lastSwap: null,
      error: null,
    });
  },

  setBordereauMode: (mode) => set({ bordereauMode: mode }),

  setLockedDocumentIds: (ids) => set({ lockedDocumentIds: new Set(ids) }),

  setParentBordereauId: (id) => set({ parentBordereauId: id }),

  setUploadedParentBordereauFile: (file) => set({ uploadedParentBordereauFile: file }),

  closePanel: () => set({ isOpen: false }),

  requestSectionNavigation: (section) => set({ pendingSectionNavigation: section }),
  clearSectionNavigationRequest: () => set({ pendingSectionNavigation: null }),

  goToStep: (step) => {
    const state = get();
    // Validate navigation is allowed
    if (state.canProceedToStep(step) || step < state.currentStep) {
      set({ currentStep: step });
    }
  },

  nextStep: () => {
    const { currentStep, canProceedToStep } = get();
    const next = (currentStep + 1) as BordereauStep;
    if (currentStep < 3 && canProceedToStep(next)) {
      set({ currentStep: next });
    }
  },

  prevStep: () => {
    const { currentStep } = get();
    if (currentStep > 1) {
      set({ currentStep: (currentStep - 1) as BordereauStep });
    }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // DOCUMENT ACTIONS
  // ═══════════════════════════════════════════════════════════════════════════

  reorderDocuments: (newOrder) => set({ documentOrder: newOrder }),

  setRenumberOnDrag: (value) => set({ renumberOnDrag: value }),

  /**
   * Réordonne l'affichage ET renumérote en repartant du numéro que portait la
   * tête de liste avant le déplacement (`reflowAfterReorder`) : une pièce glissée
   * en première position devient la n° 1. L'échappatoire `renumberOnDrag = false`
   * (côté NumberingStep) retombe sur `reorderDocuments`.
   *
   * Deux garde-fous :
   *  - **Récapitulatif (locked)** : la numérotation parente est FIGÉE et cumulative
   *    (Conseil d'État) — un glisser-déposer ne doit jamais la rebattre. On se
   *    contente alors de réordonner l'affichage.
   *  - **Pièces exclues** : elles ne portent pas de numéro → sautées dans la
   *    séquence (leur entrée `pieceNumbers` reste inchangée). La norme vient de
   *    la 1ʳᵉ pièce NON exclue d'avant le déplacement.
   */
  reorderAndReflow: (newOrder) => {
    const { lockedDocumentIds, excludedDocumentIds, pieceNumbers, documentOrder } = get();

    if (lockedDocumentIds.size > 0) {
      set({ documentOrder: newOrder });
      return;
    }

    const isNumbered = (id: string) => !excludedDocumentIds.has(id);
    const idsToNumber = newOrder.filter(isNumbered);
    const seq = reflowAfterReorder(
      documentOrder.filter(isNumbered),
      idsToNumber,
      (id) => pieceNumbers[id]?.number,
    );

    const nextNumbers: Record<string, { number: string; label?: string }> = { ...pieceNumbers };
    idsToNumber.forEach((id, i) => {
      nextNumbers[id] = { number: seq[i] ?? '', label: pieceNumbers[id]?.label };
    });

    set({ documentOrder: newOrder, pieceNumbers: nextNumbers, lastSwap: null });
  },

  removeDocument: (documentId) => set((state) => {
    const { [documentId]: removed, ...remainingNumbers } = state.pieceNumbers;
    return {
      selectedDocuments: state.selectedDocuments.filter(d => d.id !== documentId),
      documentOrder: state.documentOrder.filter(id => id !== documentId),
      pieceNumbers: remainingNumbers,
    };
  }),

  addDocuments: (documents) => set((state) => ({
    selectedDocuments: [...state.selectedDocuments, ...documents],
    documentOrder: [...state.documentOrder, ...documents.map(d => d.id)],
  })),

  /**
   * Renomme une pièce dans le panel (édition inline de l'étape « Pièces »).
   * Mise à jour optimiste de `selectedDocuments` ; la persistance
   * (`PATCH /documents/:id`) et l'invalidation des queries du dossier sont
   * pilotées par `NumberingStep`, qui re-renomme avec l'ancien nom si l'API échoue.
   */
  renameDocument: (documentId, name) => set((state) => ({
    selectedDocuments: state.selectedDocuments.map((d) =>
      d.id === documentId ? { ...d, name } : d
    ),
  })),

  /**
   * Resynchronise le panel sur la DB après une écriture externe (tools agents).
   *
   * **Patch chirurgical, JAMAIS `openPanel`** : `openPanel` remet `currentStep: 1`,
   * `bordereauMode`, `parentBordereauId`, `uploadedParentBordereauFile` — il
   * ramènerait un avocat de l'étape 2 à l'étape 1 et lui ferait perdre son mode
   * Télérecours. On ne touche donc QUE les numéros et les noms.
   *
   * **`selectedDocuments[].piece_number` est le vecteur du bug, pas seulement
   * `pieceNumbers`** : `savePieceNumbers` (`BordereauPanel`) retombe dessus pour
   * tout document que l'avocat n'a pas touché, et `toggleDocumentExclusion` le
   * relit pour restaurer le numéro d'une pièce ré-incluse. Le laisser périmé
   * garderait le bug intact malgré le reseed.
   *
   * Ne mute rien si les valeurs DB sont déjà celles du store : sans cette garde,
   * un refetch identique produirait un nouvel objet state à chaque invalidation
   * et re-rendrait le panel pendant que l'avocat numérote.
   */
  reseedFromDatabase: (fresh) => set((state) => {
    // Numérotation récap figée (contrainte Conseil d'État, cf. lockedDocumentIds) :
    // le panel fait autorité, on ne la réaligne jamais sur la DB.
    if (state.lockedDocumentIds.size > 0) return {};

    const freshById = new Map(fresh.map((d) => [d.id, d]));

    let changed = false;
    const nextDocuments = state.selectedDocuments.map((doc) => {
      const dbDoc = freshById.get(doc.id);
      if (!dbDoc) return doc;

      const dbNumber = dbDoc.piece_number?.trim() || null;
      const currentNumber = doc.piece_number?.trim() || null;
      if (dbNumber === currentNumber && dbDoc.name === doc.name) return doc;

      changed = true;
      return { ...doc, piece_number: dbNumber, name: dbDoc.name };
    });

    if (!changed) return {};

    // Les numéros que l'avocat a saisis à la main dans CE panel restent
    // prioritaires : le reseed ne réaligne que les pièces qu'il n'a pas éditées.
    // `pieceNumbers[id]` n'existe que si une valeur a été seedée à l'ouverture ou
    // posée depuis ; on la remplace uniquement quand elle correspond encore à
    // l'ancienne valeur DB (donc non éditée localement).
    const nextNumbers = { ...state.pieceNumbers };
    for (const doc of state.selectedDocuments) {
      const dbDoc = freshById.get(doc.id);
      if (!dbDoc) continue;
      if (state.excludedDocumentIds.has(doc.id)) continue;

      const dbNumber = dbDoc.piece_number?.trim() || '';
      const localNumber = state.pieceNumbers[doc.id]?.number ?? '';
      const previousDbNumber = doc.piece_number?.trim() || '';

      // Édité localement (diverge de l'ancienne valeur DB) → on n'écrase pas.
      if (localNumber !== previousDbNumber) continue;

      if (dbNumber) nextNumbers[doc.id] = { number: dbNumber };
      else delete nextNumbers[doc.id];
    }

    return {
      selectedDocuments: nextDocuments,
      pieceNumbers: nextNumbers,
      documentOrder: sortDocumentOrder(state.documentOrder, nextNumbers),
      // Un swap en attente référence des numéros qui viennent de changer sous
      // lui : le garder ferait proposer une annulation vers un état qui n'existe
      // plus.
      lastSwap: null,
    };
  }),

  toggleDocumentExclusion: (documentId) => {
    const state = get();
    const next = new Set(state.excludedDocumentIds);
    const isExcluding = !next.has(documentId);

    if (isExcluding) {
      next.add(documentId);

      // Exclure une pièce ne renumérote PAS les autres : chaque pièce conserve le
      // numéro posé par l'avocat (trous autorisés), exactement comme le
      // drag-reorder et le tri chronologique (Feature 3, 2026-07-02). Seul le
      // bouton « Numéroter » réassigne 1..N. On retire UNIQUEMENT le numéro de la
      // pièce exclue.
      // Correctif 2026-07-08 : le re-séquençage auto (1,2,3…) avait été oublié
      // dans le refactor Feature 3 — il contredisait le comportement des autres
      // opérations de la liste et écrasait la numérotation stable voulue par
      // l'avocat dès qu'il excluait une pièce.
      const { [documentId]: _excludedNumber, ...remainingNumbers } = state.pieceNumbers;
      set({ excludedDocumentIds: next, pieceNumbers: remainingNumbers, lastSwap: null });
    } else {
      next.delete(documentId);

      // Restore original DB number for re-included doc
      const doc = state.selectedDocuments.find((d) => d.id === documentId);
      const originalNumber = doc?.piece_number?.trim() || '';
      const newNumbers = {
        ...state.pieceNumbers,
        [documentId]: { number: originalNumber },
      };
      set({ excludedDocumentIds: next, pieceNumbers: newNumbers, lastSwap: null });
    }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // STAMP CONFIGURATION
  // ═══════════════════════════════════════════════════════════════════════════

  setStamps: (stamps) => {
    const defaultStamp = stamps.find(s => s.is_default);
    const selectedId = defaultStamp?.id ?? stamps[0]?.id ?? null;
    set({
      availableStamps: stamps,
      selectedStampId: selectedId,
    });
  },

  selectStamp: (stampId) => {
    set({ selectedStampId: stampId });
  },

  setStampAbsolutePosition: (position) => set({ stampAbsolutePosition: position }),

  setShowNumberInStamp: (show) => set({ showNumberInStamp: show }),

  setStampAllPages: (value) => set({ stampAllPages: value }),

  setStampPerPiecePosition: (documentId, position) => set((state) => ({
    stampPerPiecePositions: { ...state.stampPerPiecePositions, [documentId]: position },
  })),

  clearStampPerPiecePosition: (documentId) => set((state) => {
    const { [documentId]: _, ...rest } = state.stampPerPiecePositions;
    return { stampPerPiecePositions: rest };
  }),

  resetAllPerPiecePositions: () => set({ stampPerPiecePositions: {} }),

  // ═══════════════════════════════════════════════════════════════════════════
  // NUMBERING
  // ═══════════════════════════════════════════════════════════════════════════

  setNumberingMode: (mode) => set({ numberingMode: mode }),

  setPieceNumber: (documentId, number, label) => {
    const { pieceNumbers } = get();
    const trimmed = number.trim();
    const newPieceNumbers = { ...pieceNumbers };
    let swap: BordereauState['lastSwap'] = null;

    // Auto-swap prevents duplicates: entering a taken number swaps instead of conflicting
    if (trimmed) {
      const existingOwner = Object.entries(newPieceNumbers).find(
        ([id, info]) => id !== documentId && info.number === trimmed
      );
      if (existingOwner) {
        const [ownerId, ownerInfo] = existingOwner;
        const oldNumber = newPieceNumbers[documentId]?.number || '';
        newPieceNumbers[ownerId] = { ...ownerInfo, number: oldNumber };
        swap = { fromDocId: documentId, toDocId: ownerId, fromNumber: oldNumber, toNumber: trimmed };
      }
    }

    newPieceNumbers[documentId] = { number: trimmed, label };

    set({ pieceNumbers: newPieceNumbers, lastSwap: swap });
  },

  applyQuickNumbering: (pattern) => {
    const { documentOrder, excludedDocumentIds, pieceNumbers, lockedDocumentIds } = get();
    const newNumbers: Record<string, { number: string }> = {};

    // En mode récapitulatif (Droit Public S3), les pièces du bordereau parent
    // ont déjà un numéro figé qui ne doit JAMAIS être écrasé. On les copie
    // verbatim depuis pieceNumbers et on démarre la séquence des nouvelles
    // pièces à `lockedDocumentIds.size + 1`. Bug rattrapé au review 2026-06-20 :
    // sans cette garde, un drag-reorder rebaschait la numérotation et brisait
    // la séquence Télérecours.
    let counter = lockedDocumentIds.size;
    for (const lockedId of lockedDocumentIds) {
      const existing = pieceNumbers[lockedId];
      if (existing) newNumbers[lockedId] = { number: existing.number };
    }

    // NB 2026-07-17 : l'auto-placement « Décision attaquée en tête »
    // (R. 412-1 CJA, détection par dossier) a été retiré avec la suppression
    // du dossier « Décision(s) attaquée(s) » et de la fusion Télérecours —
    // le bordereau ne reçoit plus que Nos pièces, la numérotation suit
    // documentOrder tel quel.
    const assignNumber = (docId: string, idx: number) => {
      switch (pattern) {
        case 'numeric':
          newNumbers[docId] = { number: String(idx) };
          break;
        case 'alpha':
          newNumbers[docId] = { number: String.fromCharCode(64 + idx) };
          break;
        case 'piece':
          newNumbers[docId] = { number: `Pièce ${idx}` };
          break;
      }
    };

    documentOrder.forEach((docId) => {
      if (excludedDocumentIds.has(docId)) return;
      if (lockedDocumentIds.has(docId)) return; // already carried over
      counter++;
      assignNumber(docId, counter);
    });

    set({ pieceNumbers: newNumbers });
  },

  clearAllNumbers: () => {
    const { documentOrder } = get();
    const cleared: Record<string, { number: string }> = {};
    documentOrder.forEach((id) => { cleared[id] = { number: '' }; });
    set({ pieceNumbers: cleared, lastSwap: null });
  },

  clearLastSwap: () => set({ lastSwap: null }),

  // ═══════════════════════════════════════════════════════════════════════════
  // GENERATION
  // ═══════════════════════════════════════════════════════════════════════════

  setGenerationStatus: (status) => set({ generationStatus: status }),

  setGenerationJobId: (jobId) => set({ generationJobId: jobId }),

  setGenerationProgress: (progress, docName) => set({
    generationProgress: progress,
    currentGeneratingDoc: docName,
  }),

  setDownloadUrl: (url) => set({ downloadUrl: url }),

  resetGeneration: () => set({
    generationStatus: 'not_started',
    generationJobId: null,
    generationProgress: 0,
    currentGeneratingDoc: '',
    downloadUrl: null,
  }),

  // ═══════════════════════════════════════════════════════════════════════════
  // OPTIONS
  // ═══════════════════════════════════════════════════════════════════════════

  setIncludeIndexPage: (include) => set({ includeIndexPage: include }),

  // ═══════════════════════════════════════════════════════════════════════════
  // COVER PAGE METADATA (TICKET-259)
  // ═══════════════════════════════════════════════════════════════════════════

  setAdminCoverMetadataField: (field, value) => set((state) => ({
    adminCoverMetadata: { ...state.adminCoverMetadata, [field]: value },
  })),

  // Les deux `reset*CoverMetadata` remettent AUSSI le souvenir de
  // pré-remplissage à zéro : il ne parle que de la valeur qu'on efface ici, et
  // le champ redevenu vierge doit pouvoir recevoir la proposition du profil.
  resetAdminCoverMetadata: () => set((state) => ({
    adminCoverMetadata: {},
    avocatPrefillOffered: { ...state.avocatPrefillOffered, admin: false },
  })),

  setCoverPageMetadataField: (field, value) => set((state) => ({
    coverPageMetadata: { ...state.coverPageMetadata, [field]: value },
  })),

  resetCoverPageMetadata: () => set((state) => ({
    coverPageMetadata: {},
    avocatPrefillOffered: { ...state.avocatPrefillOffered, civil: false },
  })),

  markAvocatPrefillOffered: (form) => set((state) => (
    // Idempotent : l'effet de pré-remplissage se rejoue à chaque remontage de
    // l'étape 3. Renvoyer l'état inchangé évite une notification Zustand qui
    // ferait re-évaluer tous les sélecteurs du panel pour rien.
    state.avocatPrefillOffered[form]
      ? state
      : { avocatPrefillOffered: { ...state.avocatPrefillOffered, [form]: true } }
  )),

  setRequestedFormat: (format) => set({ requestedFormat: format }),

  // ═══════════════════════════════════════════════════════════════════════════
  // ERROR HANDLING
  // ═══════════════════════════════════════════════════════════════════════════

  setError: (error) => set({ error }),

  clearError: () => set({ error: null }),

  // ═══════════════════════════════════════════════════════════════════════════
  // SYNC WITH BACKEND
  // ═══════════════════════════════════════════════════════════════════════════

  setBordereau: (bordereau) => set((state) => ({
    bordereau,
    bordereauId: bordereau.id,
    // Only set selectedStampId if not already selected by user
    // This prevents overwriting user's stamp selection when bordereau is updated
    selectedStampId: state.selectedStampId || bordereau.stamp_id,
  })),

  setBordereauId: (id) => set({ bordereauId: id }),

  // ═══════════════════════════════════════════════════════════════════════════
  // VALIDATION HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  canProceedToStep: (step) => {
    const state = get();

    const hasAtLeastOneNumberedPiece = () => {
      if (Object.values(state.pieceNumbers).some(p => p.number && p.number.trim())) return true;
      return state.selectedDocuments.some(doc => {
        const overridden = doc.id in state.pieceNumbers;
        return !overridden && doc.piece_number && doc.piece_number.trim() !== '';
      });
    };

    switch (step) {
      case 1:
        return state.selectedDocuments.length > 0;
      case 2:
        return hasAtLeastOneNumberedPiece();
      case 3:
        return hasAtLeastOneNumberedPiece();
      default:
        return false;
    }
  },

  getNumberedPiecesCount: () => {
    const { pieceNumbers } = get();
    return Object.values(pieceNumbers).filter(p => p.number && p.number.trim()).length;
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // RESET
  // ═══════════════════════════════════════════════════════════════════════════

  reset: () => set(initialState),
}));
